import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export interface LightboxImage {
  src: string;
  label: string;
}

interface LightboxProps {
  image: LightboxImage;
  index: number;
  total: number;
  images: LightboxImage[];
  onPrev: () => void;
  onNext: () => void;
  onClose: () => void;
}

/* ─── swipe / pinch constants ───────────────────────── */
const SWIPE_THRESHOLD = 50;   // px horizontal to trigger nav
const SWIPE_VELOCITY = 0.3;   // minimum px/ms
const PINCH_SCALE_MIN = 0.6;
const PINCH_SCALE_MAX = 3;

/* ─── preloaded image cache ─────────────────────────── */
const preloadedSet = new Set<string>();

const preloadImage = (src: string) => {
  if (preloadedSet.has(src)) return;
  preloadedSet.add(src);
  const img = new Image();
  img.src = src;
};

const Lightbox: React.FC<LightboxProps> = ({ image, index, total, images, onPrev, onNext, onClose }) => {
  /* ── keyboard ─────────────────────────────────────── */
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, onPrev, onNext]);

  /* ── preload adjacent images ──────────────────────── */
  useEffect(() => {
    if (total <= 1) return;

    const prevIdx = (index - 1 + total) % total;
    const nextIdx = (index + 1) % total;

    // Preload prev and next
    preloadImage(images[prevIdx].src);
    preloadImage(images[nextIdx].src);

    // Also preload two steps ahead in both directions for fast swipers
    if (total > 3) {
      preloadImage(images[(index - 2 + total) % total].src);
      preloadImage(images[(index + 2) % total].src);
    }

    // Inject <link rel="preload"> for browser-level prioritization
    const head = document.head;
    const links: HTMLLinkElement[] = [];

    [images[prevIdx].src, images[nextIdx].src].forEach((src) => {
      // Skip if already has a preload link
      if (head.querySelector(`link[rel="preload"][href="${src}"]`)) return;
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = src;
      head.appendChild(link);
      links.push(link);
    });

    return () => {
      links.forEach((link) => link.remove());
    };
  }, [index, total, images]);

  /* ── touch / swipe state ──────────────────────────── */
  const touchRef = useRef<{ x: number; y: number; t: number } | null>(null);
  const pinchRef = useRef<{ startDist: number; startScale: number } | null>(null);
  const [dragOffset, setDragOffset] = useState(0);
  const [zoom, setZoom] = useState(1);

  /* helpers */
  const getDistance = (a: { clientX: number; clientY: number }, b: { clientX: number; clientY: number }) =>
    Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      /* ── pinch start ── */
      pinchRef.current = { startDist: getDistance(e.touches[0], e.touches[1]), startScale: zoom };
    } else if (e.touches.length === 1) {
      /* ── swipe start ── */
      touchRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, t: Date.now() };
    }
  }, [zoom]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2 && pinchRef.current) {
      /* ── pinch move → zoom ── */
      const dist = getDistance(e.touches[0], e.touches[1]);
      const scale = pinchRef.current.startScale * (dist / pinchRef.current.startDist);
      setZoom(Math.min(PINCH_SCALE_MAX, Math.max(PINCH_SCALE_MIN, scale)));
    } else if (e.touches.length === 1 && touchRef.current && zoom === 1) {
      /* ── swipe move → drag feedback ── */
      const dx = e.touches[0].clientX - touchRef.current.x;
      setDragOffset(dx);
    }
  }, [zoom]);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 0 && touchRef.current) {
      const dx = e.changedTouches[0].clientX - touchRef.current.x;
      const dt = Date.now() - touchRef.current.t;
      const velocity = Math.abs(dx) / dt;

      if (Math.abs(dx) > SWIPE_THRESHOLD || velocity > SWIPE_VELOCITY) {
        if (dx > 0) onPrev();
        else onNext();
      }
      touchRef.current = null;
      setDragOffset(0);
    }
    if (e.touches.length < 2) {
      pinchRef.current = null;
    }
  }, [onPrev, onNext]);

  /* reset zoom when image changes */
  useEffect(() => {
    setZoom(1);
    setDragOffset(0);
  }, [image.src]);

  /* swipe direction indicator */
  const swipeIndicator = useMemo(() => {
    if (total <= 1) return null;
    const prevIdx = (index - 1 + total) % total;
    const nextIdx = (index + 1) % total;
    return { prev: images[prevIdx]?.label, next: images[nextIdx]?.label };
  }, [index, total, images]);

  return (
    <motion.div
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* backdrop */}
      <motion.div
        className="absolute inset-0 bg-[#06150e]/92 backdrop-blur-xl"
        onClick={onClose}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      />

      {/* decorative glow rings */}
      <motion.div
        className="pointer-events-none absolute left-1/2 top-1/2 h-[600px] w-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full border border-[#d4af37]/10"
        animate={{ rotate: 360, scale: [1, 1.08, 1] }}
        transition={{ rotate: { duration: 40, repeat: Infinity, ease: 'linear' }, scale: { duration: 6, repeat: Infinity, ease: 'easeInOut' } }}
      />
      <motion.div
        className="pointer-events-none absolute left-1/2 top-1/2 h-[420px] w-[420px] -translate-x-1/2 -translate-y-1/2 rounded-full border border-[#d4af37]/15"
        animate={{ rotate: -360, scale: [1, 1.12, 1] }}
        transition={{ rotate: { duration: 30, repeat: Infinity, ease: 'linear' }, scale: { duration: 4.5, repeat: Infinity, ease: 'easeInOut' } }}
      />

      {/* close button */}
      <button
        onClick={onClose}
        className="absolute right-6 top-6 z-10 flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/10 text-white text-lg font-bold backdrop-blur transition hover:bg-[#d4af37] hover:text-[#06150e]"
        aria-label="Close"
      >
        ✕
      </button>

      {/* counter */}
      {total > 1 && (
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 rounded-full border border-white/10 bg-white/[0.06] px-5 py-2 text-xs font-extrabold tracking-widest text-white/60 backdrop-blur">
          {index + 1} / {total}
        </div>
      )}

      {/* swipe hint — shown briefly on mobile */}
      {total > 1 && (
        <motion.div
          className="absolute bottom-20 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-white/30 md:hidden"
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 0.6, 0] }}
          transition={{ duration: 2.5, delay: 0.5 }}
        >
          ← swipe →
        </motion.div>
      )}

      {/* prev arrow */}
      {total > 1 && (
        <button
          onClick={onPrev}
          className="absolute left-4 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-white/12 bg-white/[0.06] text-white text-xl font-bold backdrop-blur transition hover:bg-[#d4af37] hover:text-[#06150e] max-md:hidden"
          aria-label={swipeIndicator?.prev ? `Previous: ${swipeIndicator.prev}` : 'Previous'}
        >
          ‹
        </button>
      )}

      {/* next arrow */}
      {total > 1 && (
        <button
          onClick={onNext}
          className="absolute right-4 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-white/12 bg-white/[0.06] text-white text-xl font-bold backdrop-blur transition hover:bg-[#d4af37] hover:text-[#06150e] max-md:hidden"
          aria-label={swipeIndicator?.next ? `Next: ${swipeIndicator.next}` : 'Next'}
        >
          ›
        </button>
      )}

      {/* image with decorative frame */}
      <AnimatePresence mode="wait">
        <motion.div
          key={image.src}
          className="relative z-10 mx-16 max-h-[80vh] max-w-4xl touch-none"
          style={{ transform: `translateX(${dragOffset}px) scale(${zoom})` }}
          initial={{ opacity: 0, scale: 0.88, y: 40, rotateX: 8 }}
          animate={{ opacity: 1, scale: 1, y: 0, rotateX: 0, x: 0 }}
          exit={{ opacity: 0, scale: 0.92, y: -30 }}
          transition={{ type: 'spring', stiffness: 260, damping: 28 }}
        >
          {/* glow behind image */}
          <div className="absolute -inset-6 rounded-[2rem] bg-gradient-to-br from-[#d4af37]/20 via-transparent to-[#14532d]/25 blur-3xl" />

          <div className="relative overflow-hidden rounded-[1.5rem] border border-white/15 shadow-2xl shadow-black/60">
            <img
              src={image.src}
              alt={image.label}
              className="block max-h-[75vh] w-full rounded-[1.5rem] object-contain select-none"
              draggable={false}
            />
          </div>

          {/* label badge */}
          <motion.p
            className="absolute -bottom-4 left-1/2 -translate-x-1/2 rounded-full border border-[#d4af37]/30 bg-[#06150e]/90 px-6 py-2 text-xs font-extrabold uppercase tracking-[0.18em] text-[#d4af37] shadow-xl backdrop-blur"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            {image.label}
          </motion.p>
        </motion.div>
      </AnimatePresence>
    </motion.div>
  );
};

export default Lightbox;
